/**
 * @file useCompleteConfirm.js
 * @location src/hooks/useCompleteConfirm.js
 * @role 미래 예정 완료 시 확인 다이얼로그 패턴 훅 (TeamTaskTable 체크박스에서 사용)
 *
 * requestToggle(task)  미래 예정면 confirmTask 설정(모달), 아니면 handleToggle(task.id) 직접
 * confirmTask          확인 대상 task (null = 모달 닫힘)
 * onConfirm()          안 읽음이면 ACK_TASK 자동 → handleToggle → 닫기
 * onCancel()           confirmTask=null
 * handleToggle(id)     useTaskActions 위임(루틴 카드 등 직접 토글용 재노출)
 *
 * 조기기 완료(due 전 완료)는 이례적이라 확인을 받는다. 읽음 게이트(handshake)는 유지 —
 * 「완료-하기」가 명시적 의사라 그 자리에서 자동 읽음 후 완료(우회 아님).
 */
import { useCallback, useState } from 'react';
import { useTaskActions } from './useTaskActions';
import { isFutureYotei } from '../utils/taskSelectors';
import { getTodayStr, fmtShort, ddayLabel } from '../utils/dateUtils';

/**
 * 예정 업무 완료 확인 문구.
 * @param {{ due?: string|null }} task
 * @param {string} today YYYY-MM-DD
 * @returns {string}
 */
export function completeConfirmMsg(task, today) {
  return `이 업무는 ${fmtShort(task.due)}(${ddayLabel(task.due, today).label}) 예정입니다. 지금 완료로 처리하시겠습니까?`;
}

export function useCompleteConfirm(onToast) {
  const { handleToggle } = useTaskActions(onToast);
  const [confirmTask, setConfirmTask] = useState(null);

  const requestToggle = useCallback((task) => {
    if (isFutureYotei(task, getTodayStr())) setConfirmTask(task);
    else handleToggle(task.id);
  }, [handleToggle]);

  // 읽음 게이트(handshake)는 handleToggle 이 단일 지점에서 처리 — 여기선 토글만.
  const onConfirm = useCallback(() => {
    if (!confirmTask) return;
    handleToggle(confirmTask.id);
    setConfirmTask(null);
  }, [confirmTask, handleToggle]);

  const onCancel = useCallback(() => setConfirmTask(null), []);

  return { requestToggle, handleToggle, confirmTask, onConfirm, onCancel };
}
